import { useCallback, useEffect, useState } from 'react';

export const SHOP_PILE_KEY = 'gb.shop.pile';
export const LIBRARY_PILE_KEY = 'gb.library.pile';

function readOrder(key: string): string[] {
  if (typeof window === 'undefined') return [];
  try {
    const kept: unknown = JSON.parse(window.localStorage.getItem(key) ?? '[]');
    return Array.isArray(kept)
      ? kept.filter((slug): slug is string => typeof slug === 'string')
      : [];
  } catch {
    return [];
  }
}

function keepOrder(key: string, order: readonly string[]): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(key, JSON.stringify(order));
  } catch {
    return;
  }
}

export function pileFrom<Item>(
  items: readonly Item[],
  slugOf: (item: Item) => string,
  remembered: readonly string[],
): Item[] {
  const bySlug = new Map(items.map((item) => [slugOf(item), item]));
  const handled = remembered
    .map((slug) => bySlug.get(slug))
    .filter((item): item is Item => item !== undefined);
  const handledSlugs = new Set(handled.map(slugOf));
  const untouched = items.filter((item) => !handledSlugs.has(slugOf(item)));
  return [...untouched.reverse(), ...handled];
}

export interface PileOrder {
  order: readonly string[];
  putOnTop: (slug: string | null) => void;
}

export function usePileOrder(key: string): PileOrder {
  const [order, setOrder] = useState<string[]>(() => readOrder(key));

  useEffect(() => {
    keepOrder(key, order);
  }, [key, order]);

  const putOnTop = useCallback((slug: string | null) => {
    if (slug === null) return;
    setOrder((kept) => [...kept.filter((seen) => seen !== slug), slug]);
  }, []);

  return { order, putOnTop };
}
